import { useEffect, useMemo, useState } from 'react'

import { useQueries, useQueryClient } from '@tanstack/react-query'
import type { QueryKey } from '@tanstack/react-query'

import { combinePerItemSearchResults } from '@/hooks/search/lib'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginatedResponse } from '@/types/Api'

interface UseCodeSearchArgs<T> {
  initialCodes: string[]
  searchQueryKeyFn: (query: string) => QueryKey
  itemQueryKeyFn: (code: string) => QueryKey
  fetchSearch: (
    query: string,
    accessToken: string,
  ) => Promise<PaginatedResponse<T>>
  fetchItem: (code: string, accessToken: string) => Promise<T | undefined>
  itemIdFn: (item: T) => string
}

export const useCodeSearch = <T>({
  initialCodes,
  searchQueryKeyFn,
  itemQueryKeyFn,
  fetchSearch,
  fetchItem,
  itemIdFn,
}: UseCodeSearchArgs<T>) => {
  const { accessToken } = useAuthInfoWithThrow()
  const queryClient = useQueryClient()

  // Debounce the query key, not the fetch function. Clearing is instant.
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedTerm, setDebouncedTerm] = useState('')

  useEffect(() => {
    if (searchTerm === '') {
      setDebouncedTerm('')
      return
    }
    const id = setTimeout(() => setDebouncedTerm(searchTerm), 150)
    return () => clearTimeout(id)
  }, [searchTerm])

  const combine = useMemo(
    () =>
      combinePerItemSearchResults<T>({
        searchTerm: debouncedTerm,
        queryClient,
        itemIdFn,
        searchQueryKeyFn,
        itemCount: initialCodes.length,
      }),
    [
      debouncedTerm,
      initialCodes.length,
      queryClient,
      itemIdFn,
      searchQueryKeyFn,
    ],
  )

  // Stable serialization so unstable array identity from callers doesn't
  // recompute per-item queries on every render.
  const initialCodesKey = initialCodes.toSorted().join(',')

  const queries = useMemo(
    () => [
      ...initialCodes.map((code) => ({
        queryKey: itemQueryKeyFn(code),
        queryFn: () => fetchItem(code, accessToken),
        staleTime: Infinity,
      })),
      {
        queryKey: searchQueryKeyFn(debouncedTerm),
        queryFn: () => fetchSearch(debouncedTerm, accessToken),
        staleTime: Infinity,
      },
    ],
    // oxlint-disable-next-line react/exhaustive-deps -- initialCodesKey is a stable serialization of initialCodes
    [
      initialCodesKey,
      accessToken,
      debouncedTerm,
      fetchItem,
      itemQueryKeyFn,
      fetchSearch,
      searchQueryKeyFn,
    ],
  )

  const result = useQueries({ queries, combine })

  return {
    ...result,
    query: searchTerm,
    setQuery: setSearchTerm,
  }
}
